import React from 'react';
import { BookPreviewSection } from './BookPreview';
import {
  HTMLContentRenderer,
  ImageContentRenderer,
  VideoContentRenderer,
  PDFContentRenderer,
  SCORMContentRenderer,
  QuizContentRenderer,
  EmbedContentRenderer,
  FileContentRenderer,
  DefaultContentRenderer
} from './renderers';

interface BookPreviewContentProps {
  currentSection?: BookPreviewSection;
  onNext: () => void;
  onPrevious: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
  isFullscreen?: boolean;
}

export const BookPreviewContent: React.FC<BookPreviewContentProps> = ({
  currentSection,
  onNext,
  onPrevious,
  canGoNext,
  canGoPrevious,
  isFullscreen = false
}) => {

  const renderContent = () => {
    if (!currentSection) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          <div className="text-center">
            <div className="text-4xl mb-4">📚</div>
            <p>Chọn một phần để xem nội dung</p>
          </div>
        </div>
      );
    }

    const { content_block, file, type } = currentSection;

    // Render based on content_block first, then fallback to file
    if (content_block) {
      return renderContentBlock(content_block, file);
    }

    if (file) {
      return <FileContentRenderer file={file} type={type} />;
    }

    return <DefaultContentRenderer currentSection={currentSection} />;
  };

  const renderContentBlock = (contentBlock: any, file: any) => {
    const { content_type, payload } = contentBlock;

    switch (content_type) {
      case 'html':
        return <HTMLContentRenderer payload={payload} />;

      case 'image':
        return <ImageContentRenderer payload={payload} file={file} />;

      case 'video':
        return <VideoContentRenderer payload={payload} file={file} />;

      case 'pdf':
        return <PDFContentRenderer payload={payload} file={file} />;

      case 'scorm':
        return <SCORMContentRenderer payload={payload} file={file} />;

      case 'quiz':
        return <QuizContentRenderer payload={payload}/>;

      case 'embed':
        return <EmbedContentRenderer payload={payload}/>;

      default:
        return <DefaultContentRenderer currentSection={currentSection} />;
    }
  };

  return (
    <div className={`book-preview-content flex-1 ${isFullscreen ? 'p-6' : 'p-4'}`}>
      <div className="max-w-4xl mx-auto">
        {/* Section Title */}
        {currentSection && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {currentSection.title}
            </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Thời lượng: {currentSection.duration} phút
              </p>
          </div>
        )}

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          {renderContent()}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={onPrevious}
            disabled={!canGoPrevious}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              canGoPrevious
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
            }`}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Trước
          </button>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {currentSection ? 'Nội dung' : 'Chọn nội dung'}
            </span>
          </div>

          <button
            onClick={onNext}
            disabled={!canGoNext}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              canGoNext
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
            }`}
          >
            Tiếp
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
